import React, { useEffect } from 'react';
import { ModalToggleState } from '../models/actionStates';
import { useRecoilValue } from 'recoil';
import { modalState, isMobileState } from '../recoil/selectors';
import { useSetModalState } from '../hooks/actions/useModalState';
import classNames from 'classnames';
import styles from './Modal.module.scss';
import vhCheck from 'vh-check';
import { ModalState } from '../models/ModalState';

interface Props extends ModalState {
  isVisible: boolean;
  zIndex?: number;
}

export const Modal: React.FC<Props> = ({
  isVisible,
  children,
  zIndex,
  height,
  width,
  minWidth,
  minHeight,
  maxWidth,
  maxHeight,
  ignoreLightBoxClick,
  tapToClose,
  onClose,
}) => {
  const modal = useRecoilValue(modalState);
  const setModal = useSetModalState();
  const isMobile = useRecoilValue(isMobileState);

  useEffect(() => {
    const close = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setModal({ ...modal, toggleState: ModalToggleState.Closed });
        onClose?.();
      }
    };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [modal, setModal, onClose]);

  const vhIphoneAdjustment: {
    isNeeded: boolean;
    value: string | number;
    vh: number;
    windowHeight: number;
    offset: number;
    recompute: () => void;
    unbind: () => void;
  } = vhCheck();
  return (
    <div
      className={classNames(styles.hlPrimaryModalContainer, {
        [styles.open]: isVisible,
        [styles.iphone100vh]: isMobile && height === '100vh' && vhIphoneAdjustment.isNeeded,
      })}
      style={{ zIndex: zIndex ?? 10001 }}
      onClick={() => {
        if (ignoreLightBoxClick) return;
        setModal({ ...modal, toggleState: ModalToggleState.Closed });
        onClose?.();
      }}
      role="none" // will remove the semantic meaning of an element while still exposing it to assistive technology.
    >
      <div
        className={styles.hlPrimaryModal}
        style={{
          // if mobile and 100vh, set the modal container to 100vh (with iPhone adjustments as necessary) and this modal to 100%
          height: isMobile && height === '100vh' ? '100%' : height ?? '95%',
          width: width ?? '95%',
          minWidth: minWidth ?? '0',
          minHeight: minHeight ?? '0',
          maxWidth: maxWidth ?? '100%',
          maxHeight: maxHeight ?? '100%',
        }}
        onClick={(e) => {
          e.stopPropagation();
          if (tapToClose) {
            setModal({ ...modal, toggleState: ModalToggleState.Closed });
            onClose?.();
          }
        }}
        role="none" // will remove the semantic meaning of an element while still exposing it to assistive technology.
      >
        {children}
      </div>
    </div>
  );
};

export default Modal;
